// Security → Audits (migration 20261024170000).
//
// A system is one Supabase project plus every repo and Netlify site that uses it, or one repo with no database.
// `scripts/infra-inventory.mjs systems` seeds them; the app only writes notes. The Audit button queues a Security
// Audit job (task_type security_audit) for one system: the database refuses a second open audit of the same system; different systems run side by side.
// Kyle's call on each cascading foreign key is saved per system (security_cascade_calls) when he answers a
// cascade decision, and every later audit reads them from its brief.

import { supabase } from './supabase';
import { enqueueAiJob, SECURITY_AUDIT_MODEL, type AiJob } from './aiJobs';
import { loadPromptTemplate } from './prompts';
import { PROMPT_DEFAULTS, renderPrompt } from './promptTemplates.mjs';
import type { Database } from './database.types';
import type { JobBrief } from './supabase.types';

type SystemRow = Database['public']['Tables']['security_systems']['Row'];
type CallRow = Database['public']['Tables']['security_cascade_calls']['Row'];

export type SecuritySite = { id: string; name: string; url: string | null; domains: string[] };

export type SecuritySystem = Omit<SystemRow, 'kind' | 'sites'> & { kind: 'database' | 'static'; sites: SecuritySite[] };

export type CascadeCall = Omit<CallRow, 'call'> & { call: 'good' | 'bad' };

/** Security Audit jobs: the system as it was when queued, and every saved cascade call for it. */
export type SecurityAuditBrief = JobBrief & {
  security_system: Pick<SecuritySystem, 'id' | 'key' | 'name' | 'kind' | 'supabase_ref' | 'supabase_org' | 'reachable' | 'repos' | 'sites' | 'client_name' | 'notes'>;
  cascade_calls: Pick<CascadeCall, 'constraint_name' | 'from_table' | 'to_table' | 'call' | 'note' | 'decided_at'>[];
};

/** Statuses that end an audit. Anything else is open and blocks the next one. */
export const CLOSED_AUDIT_STATUSES: AiJob['status'][] = ['complete', 'submitted', 'cancelled'];
export const isOpenAudit = (status: string | null | undefined) => !!status && !CLOSED_AUDIT_STATUSES.includes(status as AiJob['status']);

export const AUDIT_JOB_FIELDS = 'id, title, status, created_at, updated_at, security_system_id, operator_summary';
export type AuditJob = Pick<AiJob, 'id' | 'title' | 'status' | 'created_at' | 'updated_at' | 'security_system_id' | 'operator_summary'>;

export async function fetchSystems(): Promise<SecuritySystem[]> {
  const { data, error } = await supabase.from('security_systems').select('*').is('gone_at', null).order('name');
  if (error) throw error;
  return (data ?? []) as unknown as SecuritySystem[];
}

/** Every Security Audit job, newest first: history per system and the one open audit, if any. */
export async function fetchAuditJobs(): Promise<AuditJob[]> {
  const { data, error } = await supabase
    .from('ai_jobs')
    .select(AUDIT_JOB_FIELDS)
    .eq('task_type', 'security_audit')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as AuditJob[];
}

export async function fetchCascadeCalls(systemId: string): Promise<CascadeCall[]> {
  const { data, error } = await supabase.from('security_cascade_calls').select('*').eq('system_id', systemId).order('from_table');
  if (error) throw error;
  return (data ?? []) as CascadeCall[];
}

export async function setSystemNotes(id: string, notes: string | null): Promise<void> {
  const { data, error } = await supabase.from('security_systems').update({ notes }).eq('id', id).select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('Nothing was saved. Only admins can write notes.');
}

export async function updateCascadeCall(id: string, patch: { call?: 'good' | 'bad'; note?: string | null }, userId: string | null): Promise<void> {
  const { data, error } = await supabase
    .from('security_cascade_calls')
    .update({ ...patch, decided_by: userId, decided_at: new Date().toISOString() })
    .eq('id', id)
    .select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('Nothing was saved. Only admins can change cascade calls.');
}

export async function deleteCascadeCall(id: string): Promise<void> {
  const { data, error } = await supabase.from('security_cascade_calls').delete().eq('id', id).select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('Nothing was deleted. Only admins can remove cascade calls.');
}

export const systemSites = (s: Pick<SecuritySystem, 'sites'>): SecuritySite[] => (Array.isArray(s.sites) ? s.sites : []);

/** Where a site runs: its first custom domain, else its Netlify URL. */
export const siteUrl = (site: SecuritySite): string | null => (site.domains?.[0] ? `https://${site.domains[0]}` : site.url);

export function databaseLine(s: Pick<SecuritySystem, 'kind' | 'supabase_ref' | 'supabase_org' | 'reachable'>): string {
  if (s.kind === 'static' || !s.supabase_ref) return 'none (static: codebase and live config checks only)';
  return `${s.supabase_ref}${s.supabase_org ? ` (org ${s.supabase_org})` : ''}${s.reachable ? '' : ' — NOT reachable with our management token: say so and audit only what is public'}`;
}

/** The "security-audit" prompt (Agents → Prompts). */
export function formatSecurityAuditNote(system: SecurityAuditBrief['security_system'], calls: number, template: string = PROMPT_DEFAULTS['security-audit']): string {
  const sites = systemSites(system);
  return renderPrompt(template, {
    system: system.name,
    system_id: system.id,
    kind: system.kind === 'database' ? 'database system' : 'static: no database',
    client: system.client_name,
    database: databaseLine(system),
    repos: system.repos.length ? system.repos.map((r) => `- ${r}`).join('\n') : '- (none: a database no repo points at)',
    sites: sites.length ? sites.map((x) => `- ${x.name}${siteUrl(x) ? ` — ${siteUrl(x)}` : ''}`).join('\n') : '- (none)',
    cascade_calls: calls ? `${calls} saved` : 'none yet',
  });
}

/** The open audit's message when one is running, for the disabled Audit button and the insert error. */
export function runningLabel(job: AuditJob | null | undefined, systems: SecuritySystem[]): string | null {
  if (!job) return null;
  return systems.find((s) => s.id === job.security_system_id)?.name ?? job.title;
}

/** Queue a Security Audit for one system: a task to hang it on, then the job. The database refuses a second open audit of the same system. */
export async function enqueueSecurityAudit(system: SecuritySystem, createdBy: { id: string; name: string }): Promise<AiJob> {
  if (system.kind === 'database' && !system.reachable && !system.repos.length) {
    throw new Error('Nothing to audit: we have no access to this database and no repo points at it.');
  }
  const calls = await fetchCascadeCalls(system.id);
  const snapshot: SecurityAuditBrief['security_system'] = {
    id: system.id,
    key: system.key,
    name: system.name,
    kind: system.kind,
    supabase_ref: system.supabase_ref,
    supabase_org: system.supabase_org,
    reachable: system.reachable,
    repos: system.repos,
    sites: systemSites(system),
    client_name: system.client_name,
    notes: system.notes,
  };
  const brief: SecurityAuditBrief = {
    security_system: snapshot,
    cascade_calls: calls.map(({ constraint_name, from_table, to_table, call, note, decided_at }) => ({ constraint_name, from_table, to_table, call, note, decided_at })),
  };
  const title = `Security Audit — ${system.name}`;
  const queueNote = formatSecurityAuditNote(snapshot, calls.length, await loadPromptTemplate('security-audit'));

  const now = new Date();
  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: system.client_id,
      title,
      description: queueNote,
      status: 'In Progress',
      due_date: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString(),
      assigned_by: createdBy.id,
      assigned_to: createdBy.id,
      assigned_to_name: createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  try {
    const job = await enqueueAiJob({
      task_id: task.id,
      title,
      created_by: createdBy.id,
      queue_note: queueNote,
      kind: 'fulfillment',
      task_type: 'security_audit',
      security_system_id: system.id,
      brief,
      model: SECURITY_AUDIT_MODEL,
    });
    return job;
  } catch (err) {
    // The job never landed: drop the task so the client's list doesn't keep an orphan.
    await supabase.from('client_tasks').delete().eq('id', task.id);
    throw err;
  }
}
